import { registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptors, withXhr } from '@angular/common/http';
import de from '@angular/common/locales/de';
import en from '@angular/common/locales/en';
import { ErrorHandler, inject, provideAppInitializer } from '@angular/core';
import { adpScopedInterceptor } from '@app/adp-interceptor.providers';
import { translationProviders } from '@app/translation.providers';
import { dxHttpInterceptors, dxProviders } from '@core/dx/dx.providers';
import { backendUnavailableInterceptor } from '@core/interceptors/backend-unavailable.interceptor';
import { MaintenanceService } from '@core/pages/error-pages/maintenance-page/services/maintenance.service';
import { AmlDeskErrorHandlerService } from '@core/services/aml-desk-error-handler.service';
import { ChunkLoadingErrorHandlerService } from '@core/services/chunk-loading-error-handler.service';
import { AppInitializer } from '@core/utils/app-initializer';
import { environment } from '@env/environment';
import {
  BaseMaintenanceService,
  domainErrorInterceptor,
  ErrorHandlerService,
  formlyProviders,
  NOTIFICATION_OPTIONS_TOKEN,
  NotificationPositionMap,
  StorageConfigurationToken,
} from '@kerberos-compliance/compliance-shared-ui';
import { NzModalService } from 'ng-zorro-antd/modal';
import { createKerberosTokenInterceptor } from '@/auth/interceptor/kerberos.interceptor';
import { StorageServiceKeysMap } from '@/storage/types/storage.types';
import { errorHandlerProviders } from './error-handler.providers';

// eslint-disable-next-line unicorn/no-top-level-side-effects
registerLocaleData(en);
// eslint-disable-next-line unicorn/no-top-level-side-effects
registerLocaleData(de);

// ADP-host-scoped interceptors: each runs only for requests targeting the ADP platform domain.
const adpInterceptors = [
  adpScopedInterceptor(createKerberosTokenInterceptor()),
  adpScopedInterceptor(backendUnavailableInterceptor),
];

export const MinimalProviders = [
  provideAppInitializer(() => {
    const appInitializer = inject(AppInitializer);

    const chunkLoadingErrorHandler = inject(ChunkLoadingErrorHandlerService);

    chunkLoadingErrorHandler.initialize();

    return appInitializer.initialize();
  }),
  ChunkLoadingErrorHandlerService,
  ...errorHandlerProviders,
  ErrorHandler,
  {
    provide: StorageConfigurationToken,
    useValue: {
      prefix: 'ad_',
      storage: localStorage,
      keys: Object.values(StorageServiceKeysMap),
    },
  },
  {
    provide: 'ENVIRONMENT_CONFIG',
    useValue: environment.config,
  },
  provideHttpClient(withXhr(), withInterceptors([...adpInterceptors, domainErrorInterceptor, ...dxHttpInterceptors()])),
  formlyProviders,
  {
    provide: NOTIFICATION_OPTIONS_TOKEN,
    useValue: {
      placement: NotificationPositionMap.top,
    },
  },
  { provide: BaseMaintenanceService, useExisting: MaintenanceService },
  ...translationProviders,
  // After the translation providers: the DX missing-key handler overrides Transloco's
  // default, and only on builds where the i18n diagnostic can be reached.
  ...dxProviders(),
  NzModalService,
  {
    provide: ErrorHandlerService,
    useExisting: AmlDeskErrorHandlerService,
  },
];
